import { CloseButton, Divider, Flex, Group, NavLink, Stack } from "@mantine/core";
import { container, navLink, navLinkSection, bottom, navLinkLabel, top, divider, mobileMenu } from "./Navbar.module.css";
import { MenuType } from "../../types/menu";
import { useMatches, useNavigate } from "@tanstack/react-router";
import { AvatarControl } from "./AvatarControl";
import { FeedbackControl } from "./FeedbackControl";
import { HeaderIcon } from "./HeaderIcon";
import { IconDotsVertical } from "@tabler/icons-react";
import { FormattedMessage } from "react-intl";
import { useDisclosure } from "@mantine/hooks";
import type { FC, ReactNode } from "react";
import type { MenuOption, NavMenuOptions } from "../../types/menu";

interface NavbarProps {
  menu: NavMenuOptions;
}

const navLinkClassNames = {
  root: navLink,
  section: navLinkSection,
  label: navLinkLabel,
};

interface MobileMenuProps {
  menu: ReactNode[];
  open: boolean;
  onToggle: () => void;
}

const MobileMenu: FC<MobileMenuProps> = ({ menu, open, onToggle }) => {
  return (
    <>
      <NavLink
        hiddenFrom="xs"
        classNames={navLinkClassNames}
        key="More"
        active={open}
        leftSection={<IconDotsVertical />}
        label={<FormattedMessage id="navigation.more" defaultMessage="More" />}
        onClick={onToggle}
      />
      {open && (
        <Flex direction="column" hiddenFrom="xs" className={mobileMenu}>
          <Group justify="space-between" p="sm">
            <AvatarControl />
            <CloseButton onClick={onToggle} />
          </Group>
          <Divider className={divider} />
          <Stack gap="0px">
            {menu}
          </Stack>
        </Flex>
      )}
    </>
  );
};

/**
 * Remove trailing slash from fullPath
 */
function normalizeFullPath(fullPath: string) {
  return fullPath.replace(/\/$/, "");
}

export const Navbar: FC<NavbarProps> = ({ menu: { items: menuItems } }) => {
  const matches = useMatches();
  const navigate = useNavigate();
  const [opened, { toggle }] = useDisclosure();

  const [lastMatch] = matches.slice(-1);

  if (!lastMatch) {
    throw new Error("No match found, this should never happen");
  }

  const renderMenuItem = (item: MenuOption, index: number) => {
    if (item.type === MenuType.Single) {
      const {
        title,
        Icon,
        to,
        params,
        search,
        hideOnMobile,
      } = item;

      const active = !opened && normalizeFullPath(lastMatch.fullPath) === to;
      return (
        <NavLink
          visibleFrom={hideOnMobile ? "xs" : undefined}
          classNames={navLinkClassNames}
          key={title}
          active={active}
          leftSection={Icon ? <Icon /> : undefined}
          label={title}
          onClick={() => {
            if (opened) {
              toggle();
            }
            void navigate({
              to,
              params,
              search,
            });
          }}
        />
      );
    }
    else if (item.type === MenuType.Nested) {
      const {
        title,
        Icon,
        prefix,
        hideOnMobile,
        options,
      } = item;

      const [firstOption] = options;

      const active = !opened && normalizeFullPath(lastMatch.fullPath).startsWith(prefix);
      return (
        <NavLink
          classNames={navLinkClassNames}
          visibleFrom={hideOnMobile ? "xs" : undefined}
          key={title}
          label={title}
          active={active}
          leftSection={Icon ? <Icon /> : undefined}
          onClick={() => {
            if (opened) {
              toggle();
            }
            if (firstOption) {
              void navigate({
                to: firstOption.to,
                params: firstOption.params,
                search: firstOption.search,
              });
            }
          }}
        >
        </NavLink>
      );
    }
    else {
      return <Divider key={index} className={divider} />;
    }
  };

  const renderMobileMenuItem = (item: MenuOption, index: number) => {
    if (item.type === MenuType.Single) {
      const {
        title,
        Icon,
        to,
        params,
        search,
      } = item;

      const active = !opened && normalizeFullPath(lastMatch.fullPath) === to;
      return (
        <NavLink
          key={title}
          active={active}
          leftSection={Icon ? <Icon /> : undefined}
          label={title}
          onClick={() => {
            if (opened) {
              toggle();
            }
            void navigate({
              to,
              params,
              search,
            });
          }}
        />
      );
    }
    else if (item.type === MenuType.Nested) {
      const {
        title,
        Icon,
        prefix,
        options,
      } = item;

      const [firstOption] = options;

      const active = !opened && normalizeFullPath(lastMatch.fullPath).startsWith(prefix);
      return (
        <NavLink
          key={title}
          label={title}
          active={active}
          leftSection={Icon ? <Icon /> : undefined}
          onClick={() => {
            if (opened) {
              toggle();
            }
            if (firstOption) {
              void navigate({
                to: firstOption.to,
                params: firstOption.params,
                search: firstOption.search,
              });
            }
          }}
        >
        </NavLink>
      );
    }
    else {
      return <Divider key={index} className={divider} />;
    }
  };

  const mobileMenuItems = menuItems.filter(i => i.hideOnMobile);

  const desktopMenu = menuItems.map(renderMenuItem);
  const mobileMenuEls = mobileMenuItems.map(renderMobileMenuItem);

  return (
    <Flex className={container}>
      <Stack className={top}>
        <HeaderIcon />
      </Stack>

      {desktopMenu}

      <Stack className={bottom}>
        <FeedbackControl />
        <AvatarControl />
      </Stack>

      <MobileMenu
        menu={mobileMenuEls}
        open={opened}
        onToggle={toggle}
      />
    </Flex>
  );
};
